/**
 * Git-diff registry detection — FEAT-184 / BET-99.
 *
 * Detects whether files listed in the registry manifest were modified
 * since a given ref (or within the working tree). Uses conservative matching:
 * any change to a manifest-listed file triggers a coherence check.
 * False positives are cheap; false negatives are the failure mode (RH2).
 *
 * Dev-time only: uses synchronous `execSync` with a 5s timeout. Acceptable
 * because this runs at wrapup time, not in response to user actions (no-go #5).
 */

import { execSync } from "node:child_process";
import { REGISTRY_MANIFEST } from "./data.js";

/**
 * Get the set of manifest file paths that have been modified.
 * Checks both staged and unstaged changes against the working tree.
 * Returns an empty set if git is unavailable or the command fails.
 */
export function detectTouchedRegistries(projectRoot: string): Set<string> {
  if (!projectRoot) return new Set();

  const manifestPaths = new Set(REGISTRY_MANIFEST.map((r) => r.path));
  const touched = new Set<string>();

  try {
    const raw = execSync("git diff --name-only HEAD 2>/dev/null || git diff --name-only", {
      cwd: projectRoot,
      encoding: "utf-8",
      timeout: 5_000,
    });

    for (const line of raw.split("\n")) {
      const trimmed = line.trim();
      if (trimmed && manifestPaths.has(trimmed)) {
        touched.add(trimmed);
      }
    }
  } catch (err) {
    if (process.env.DEBUG_COHERENCE) {
      console.debug("[coherence] Git detection failed:", err instanceof Error ? err.message : err);
    }
  }

  return touched;
}

/**
 * Quick boolean check: did this session's working tree touch any registry file?
 */
export function hasRegistryChanges(projectRoot: string): boolean {
  return detectTouchedRegistries(projectRoot).size > 0;
}
